import type { Item } from '@finsim/model';
import { useTranslation } from 'react-i18next';
import { Field, Section, Select, useReadOnly } from '../../../shared/ui';
import { AnalyseReturnsButton } from '../../analyse-returns';
import { withOptional } from '../lib';
import type { FormContext } from './context';

const NONE = '__none';

/**
 * Returns: which investment the item belongs to (once one is scoped to tagged items), and for
 * assets a shortcut to the Returns view. Hidden when neither applies.
 */
export function InvestmentSection({ item, ctx }: { item: Item; ctx: FormContext }) {
  const { t } = useTranslation();
  const readOnly = useReadOnly();
  const { investments } = ctx.project;
  const choices = investments.filter((i) => i.scope === 'items' || i.id === item.investment);
  const shortcut = item.type === 'asset' && (item.investment !== undefined || !readOnly);
  if (choices.length === 0 && !shortcut) return null;

  return (
    <Section
      title={t('returns.section')}
      actions={shortcut ? <AnalyseReturnsButton name={item.name} itemId={item.id} /> : undefined}
    >
      {choices.length > 0 ? (
        <Field label={t('returns.investment')} hint={t('returns.investmentHint')}>
          {({ id }) => (
            <Select
              id={id}
              value={item.investment ?? NONE}
              options={[
                { value: NONE, label: t('returns.noInvestment') },
                ...choices.map((i) => ({ value: i.id, label: i.name })),
              ]}
              onValueChange={(next) => {
                const investment = next === NONE ? undefined : next;
                ctx.commit(withOptional(item, 'investment', investment), 'investment');
              }}
            />
          )}
        </Field>
      ) : (
        <p className="text-xs text-text-3">{t('returns.sectionHint')}</p>
      )}
    </Section>
  );
}
